import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, X, ArrowRight, MessageCircle, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from './OnboardingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const FILING_LABELS = {
  single: 'single', married_filing_jointly: 'married filing jointly',
  married_filing_separately: 'married filing separately', head_of_household: 'head of household',
};

const StrategyRow = ({ s, index }) => (
  <li
    className="flex items-start justify-between gap-4 py-3 border-b border-white/10 last:border-0 opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards]"
    style={{ animationDelay: `${120 + index * 80}ms` }}
    data-testid={`setup-preview-strategy-${s.id}`}
  >
    <div className="min-w-0">
      <p className="text-sm font-medium text-white">{s.title}{s.id === 'qbi' && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-300/80">already counted</span>}</p>
      <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">{s.description}</p>
    </div>
    <span className="shrink-0 font-mono text-sm text-emerald-300 tabular-nums">{s.display}</span>
  </li>
);

export const SetupPreviewCard = () => {
  const { user } = useAuth();
  const { onboardingData } = useOnboarding();
  const navigate = useNavigate();
  const progress = onboardingData?.progress;
  const dismissKey = `setupPreviewDismissed:${user?.user_id}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissKey) === '1');
  const [preview, setPreview] = useState(null);

  const eligible = !!progress?.profile_completed && !dismissed;

  useEffect(() => {
    if (!eligible) return;
    axios.get(`${API}/tax-snapshot/setup-preview`, { withCredentials: true })
      .then(r => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [eligible, progress?.updated_at]);

  if (!eligible || !preview) return null;

  const dismiss = () => { localStorage.setItem(dismissKey, '1'); setDismissed(true); };
  const personalized = preview.mode === 'personalized';

  return (
    <section className="relative overflow-hidden rounded-2xl bg-stone-900 text-white shadow-xl" data-testid="setup-preview-card">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
      <button onClick={dismiss} aria-label="Dismiss savings preview" data-testid="setup-preview-dismiss"
        className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-stone-400 transition-colors hover:bg-white/10 hover:text-white">
        <X className="h-4 w-4" />
      </button>

      <div className="relative grid gap-8 p-6 md:p-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-300" data-testid="setup-preview-mode">
            <Sparkles className="h-3.5 w-3.5" /> {personalized ? `Based on your ${preview.year} ledger` : 'Example estimate'}
          </p>
          <h2 className="mt-4 text-base md:text-lg font-semibold text-stone-200">Your savings preview</h2>
          <p className="mt-2 font-heading text-4xl font-bold tracking-tight text-white tabular-nums" data-testid="setup-preview-total">
            {money(preview.total_low)} – {money(preview.total_high)}
            <span className="ml-1 text-base font-normal text-stone-400">/ yr</span>
          </p>
          <p className="mt-3 text-sm leading-relaxed text-stone-400" data-testid="setup-preview-context">
            {personalized
              ? <>Potential additional federal savings for you as a {preview.business_label} filing {FILING_LABELS[preview.filing_status]}, with {money(preview.net_profit)} net profit recorded.</>
              : <>What a typical {preview.business_label} filing {FILING_LABELS[preview.filing_status]} and earning {money(preview.net_profit)} could save. Add your income and expenses to see your own numbers.</>}
          </p>
          <div className="mt-5 flex gap-6 text-sm">
            <div><p className="text-xs text-stone-500">Est. federal tax today</p><p className="font-mono text-white tabular-nums" data-testid="setup-preview-tax">{money(preview.estimated_tax)}</p></div>
            <div><p className="text-xs text-stone-500">Self-employment tax</p><p className="font-mono text-white tabular-nums">{money(preview.se_tax)}</p></div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button onClick={() => navigate('/strategy-advisor')} className="rounded-full bg-emerald-500 text-stone-950 hover:bg-emerald-400" data-testid="setup-preview-strategies-btn">
              See my strategies <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <Button variant="ghost" onClick={() => navigate(personalized ? '/chat' : '/transactions')}
              className="rounded-full text-stone-300 hover:bg-white/10 hover:text-white" data-testid="setup-preview-secondary-btn">
              {personalized ? <><MessageCircle className="mr-1 h-4 w-4" /> Ask an advisor</> : 'Add transactions'}
            </Button>
          </div>
        </div>

        <div>
          <ul data-testid="setup-preview-strategies">
            {preview.strategies.map((s, i) => <StrategyRow key={s.id} s={s} index={i} />)}
          </ul>
          <p className="mt-4 flex gap-2 text-[11px] leading-relaxed text-stone-500" data-testid="setup-preview-disclaimer">
            <Info className="mt-0.5 h-3 w-3 shrink-0" /> {preview.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
};

export default SetupPreviewCard;
